import React, { useState, useEffect, useRef } from 'react';
import { Upload, Image as ImageIcon, RefreshCw, Save, Clock, Trash2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const AppIconSettings = () => {
  const [currentIcon, setCurrentIcon] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadIconInfo();
    loadBackups();
  }, []);

  const loadIconInfo = async () => {
    try {
      const response = await axiosInstance.get('/app-icon/current');
      setCurrentIcon(response.data);
    } catch (error) {
      console.error('Error loading icon info:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBackups = async () => {
    try {
      const response = await axiosInstance.get('/app-icon/backups');
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error loading backups:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Bitte nur Bilddateien auswählen');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Datei zu groß (max 5MB)');
        return;
      }
      
      // Show preview
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files[0];
    if (!file) {
      toast.error('Bitte zuerst ein Bild auswählen');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axiosInstance.post('/app-icon/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('App-Icon erfolgreich aktualisiert!');
      setPreviewUrl(null);
      fileInputRef.current.value = '';
      loadIconInfo();
      loadBackups();
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleRestoreTemplate = async () => {
    if (!confirm('Möchten Sie die Vorlage wiederherstellen?')) return;
    
    try {
      await axiosInstance.post('/app-icon/restore-template');
      toast.success('Vorlage wiederhergestellt!');
      loadIconInfo();
      loadBackups();
    } catch (error) {
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const handleRestoreBackup = async (filename) => {
    if (!confirm('Möchten Sie dieses Backup wiederherstellen?')) return;
    
    try {
      await axiosInstance.post(`/app-icon/restore-backup/${filename}`);
      toast.success('Backup wiederhergestellt!');
      loadIconInfo();
      loadBackups();
    } catch (error) {
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString('de-DE');
  };

  const formatSize = (bytes) => {
    return (bytes / 1024).toFixed(1) + ' KB';
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">App-Icon Einstellungen</h1>
          <p className="text-gray-500 text-sm">Verwalten Sie das Icon Ihrer PWA-App</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Icon */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Aktuelles App-Icon
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4">
              <div className="w-32 h-32 rounded-2xl overflow-hidden shadow-lg border-2 border-gray-200">
                <img
                  src={currentIcon?.url || "/app-icon.png"}
                  alt="Aktuelles App-Icon"
                  className="w-full h-full object-cover"
                />
              </div>
              {currentIcon && (
                <div className="text-sm text-gray-500 text-center">
                  {currentIcon.size && <p>Größe: {formatSize(currentIcon.size)}</p>}
                  {currentIcon.modified ? <p>Geändert: {formatDate(currentIcon.modified)}</p> : <p>Standard-Vorlage aktiv</p>}
                </div>
              )}
              <Button variant="outline" onClick={handleRestoreTemplate} className="w-full">
                <RefreshCw className="h-4 w-4 mr-2" />
                Vorlage wiederherstellen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upload New Icon */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Neues Icon hochladen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4">
              {/* Preview or Upload Area */}
              <div 
                className="w-32 h-32 rounded-2xl overflow-hidden shadow-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#C4A084] transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="Vorschau" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-gray-400">
                    <Upload className="h-8 w-8 mx-auto mb-2" />
                    <span className="text-xs">Klicken zum Auswählen</span>
                  </div>
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />

              <div className="text-xs text-gray-500 text-center">
                <p>Empfohlen: 512x512 Pixel</p>
                <p>Format: PNG, JPG (max 5MB)</p>
              </div>

              <Button 
                onClick={handleUpload} 
                disabled={!previewUrl || uploading}
                className="w-full bg-[#8B7355] hover:bg-[#7A6548]"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Wird hochgeladen...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Als App-Icon speichern
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Backups */}
      {backups.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Letzte Backups
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {backups.map((backup) => (
                <div 
                  key={backup.filename}
                  className="flex flex-col items-center gap-2 p-2 border rounded-lg hover:border-[#C4A084] cursor-pointer transition-colors"
                  onClick={() => handleRestoreBackup(backup.filename)}
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden shadow">
                    <img
                      src={backup.url}
                      alt={backup.filename}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-xs text-gray-500 text-center">
                    {formatDate(backup.created).split(',')[0]}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-4 text-center">
              Klicken Sie auf ein Backup um es wiederherzustellen
            </p>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">💡 Hinweis</h4>
        <p className="text-sm text-blue-800">
          Nach dem Ändern des App-Icons müssen Benutzer die App neu installieren, 
          um das neue Icon zu sehen. Bei iPhones: App löschen → Safari → Teilen → "Zum Home-Bildschirm"
        </p>
      </div>
    </div>
  );
};

export default AppIconSettings;
